import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import type { BilledAmountHistoryItem, DriveOrderModel, PricesPerKm } from '@/api/main/types.gen'

dayjs.extend(utc)
dayjs.extend(timezone)

/** Share of the total order we aim to bill within a billing period. */
export const TARGET_RATIO = 0.93

/**
 * End-of-period slowdown observed in practice: over the last `count` remaining weekdays
 * (weekend days) of a period we bill roughly `factor` less than the respective daily average.
 */
export const DISCOUNT = {
  weekdays: { count: 5, factor: 0.12 },
  weekendDays: { count: 2, factor: 0.63 },
} as const

/**
 * The generated types say `number`, but the backend models these as `Decimal`,
 * so be lenient about strings on the wire.
 */
export type Lenient<T> = { [K in keyof T]: T[K] extends number ? number | string : T[K] }

export type BillingHistoryInput = Lenient<BilledAmountHistoryItem>
export type DriveOrderInput = Lenient<DriveOrderModel>
export type PricesInput = Lenient<PricesPerKm>

export interface ForecastDay {
  /** Calendar day (`YYYY-MM-DD`) of the history item, as emitted by the backend. */
  day: string
  isWeekend: boolean
  /** Day is fully over, i.e. its billed amount is final. Today counts as not past. */
  isPast: boolean
  actualCumulative: number | null
  actualIncrement: number | null
  /** Forecast running total; also set on the last past day so the two series connect. */
  forecastCumulative: number | null
  forecastIncrement: number | null
}

export interface BillingForecast {
  days: ForecastDay[]
  /** Today (`YYYY-MM-DD`) in the configured timezone. */
  today: string
  billedToDate: number
  forecast: number
  discount: number
  discountedForecast: number
  order: number
  target: number
  planning: number
  development: number
  weekdayAverage: number
  weekendAverage: number
  remainingWeekdays: number
  remainingWeekendDays: number
  elapsedDays: number
  totalDays: number
}

export const toNumber = (value: number | string | null | undefined): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/** `a / b`, or 0 when `b` is 0. */
export const ratio = (a: number, b: number): number => (b === 0 ? 0 : a / b)

const mean = (values: number[]): number =>
  values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length

/**
 * The backend sends each day as midnight in *its* configured timezone, with the offset,
 * e.g. `2026-10-01T00:00:00+02:00`. Taking the calendar date straight from the string keeps
 * the day stable no matter which timezone the browser or the frontend is configured with.
 */
export const toDayKey = (date: string): string => date.slice(0, 10)

export const isWeekendDay = (dayKey: string): boolean => {
  const weekday = new Date(`${dayKey}T00:00:00Z`).getUTCDay()
  return weekday === 0 || weekday === 6
}

export const todayKey = (now: Date, tz: string): string => dayjs(now).tz(tz).format('YYYY-MM-DD')

/** Total ordered amount of the period: ordered km × price per km over all billable kinds. */
export const computeTotalOrder = (orders: Record<string, DriveOrderInput>, prices: PricesInput): number =>
  Object.values(orders).reduce(
    (sum, order) =>
      sum +
      toNumber(order.km_doc) * toNumber(prices.kc_doc) +
      toNumber(order.km_enf) * toNumber(prices.kc_enf) +
      toNumber(order.km_tst) * toNumber(prices.kc_tst) +
      toNumber(order.km_stat) * toNumber(prices.kc_stat) +
      toNumber(order.km_spec) * toNumber(prices.kc_spec) +
      toNumber(order.km_scan) * toNumber(prices.kc_scan),
    0
  )

/**
 * Billing-period progress and an end-of-period forecast.
 *
 * Past days contribute their actual (cumulative) monitoring amounts; every remaining day,
 * today included, is projected with the average daily increment of past weekdays or
 * weekend days respectively. The forecast is capped at the order (we cannot bill more
 * than was ordered), unless the actual amount already exceeds it.
 */
export const computeBillingForecast = ({
  history,
  orders,
  prices,
  now,
  timezone: tz,
}: {
  history: BillingHistoryInput[]
  orders: Record<string, DriveOrderInput>
  prices: PricesInput
  now: Date
  timezone: string
}): BillingForecast => {
  const today = todayKey(now, tz)
  const sorted = history
    .map(item => ({
      day: toDayKey(item.date),
      monitoring: toNumber(item.billed_amount_monitoring),
      planning: toNumber(item.billed_amount_planning),
    }))
    .sort((a, b) => a.day.localeCompare(b.day))

  const order = computeTotalOrder(orders, prices)
  const past = sorted.filter(item => item.day < today)
  const lastActual = past.length > 0 ? past[past.length - 1].monitoring : 0

  const pastIncrements = past.map((item, index) => ({
    day: item.day,
    isWeekend: isWeekendDay(item.day),
    increment: item.monitoring - (index > 0 ? past[index - 1].monitoring : 0),
  }))
  const weekdayAverage = mean(pastIncrements.filter(item => !item.isWeekend).map(item => item.increment))
  const weekendAverage = mean(pastIncrements.filter(item => item.isWeekend).map(item => item.increment))

  const cap = order > 0 ? Math.max(order, lastActual) : Infinity
  let running = lastActual
  let remainingWeekdays = 0
  let remainingWeekendDays = 0

  const days: ForecastDay[] = sorted.map((item, index) => {
    const isWeekend = isWeekendDay(item.day)
    if (item.day < today) {
      const isLastPast = index === past.length - 1
      return {
        day: item.day,
        isWeekend,
        isPast: true,
        actualCumulative: item.monitoring,
        actualIncrement: pastIncrements[index].increment,
        forecastCumulative: isLastPast ? Math.min(lastActual, cap) : null,
        forecastIncrement: null,
      }
    }

    const increment = isWeekend ? weekendAverage : weekdayAverage
    if (isWeekend) remainingWeekendDays += 1
    else remainingWeekdays += 1
    running += increment
    return {
      day: item.day,
      isWeekend,
      isPast: false,
      actualCumulative: null,
      actualIncrement: null,
      forecastCumulative: Math.min(running, cap),
      forecastIncrement: increment,
    }
  })

  const discount =
    weekdayAverage * DISCOUNT.weekdays.factor * Math.min(DISCOUNT.weekdays.count, remainingWeekdays) +
    weekendAverage * DISCOUNT.weekendDays.factor * Math.min(DISCOUNT.weekendDays.count, remainingWeekendDays)

  const forecast = Math.min(running, cap)

  return {
    days,
    today,
    billedToDate: lastActual,
    forecast,
    discount,
    discountedForecast: Math.max(lastActual, Math.min(running - discount, cap)),
    order,
    target: order * TARGET_RATIO,
    // The latest computed day: the backend sends zeros from today on, so the first item can be
    // a not-yet-computed day (e.g. on the period's first day or for an upcoming period).
    planning: past.length > 0 ? past[past.length - 1].planning : 0,
    development: toNumber(prices.kc_devel),
    weekdayAverage,
    weekendAverage,
    remainingWeekdays,
    remainingWeekendDays,
    elapsedDays: past.length,
    totalDays: sorted.length,
  }
}
